import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Loader2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { Button } from '@/components/ui/button';
import { useTransactions } from '@/api/transactions/use-transactions';
import { useTrades } from '@/api/trades/use-trades';
import { useGetCustomerProfile } from '@/api/auth/use-customer-auth';
import { downloadStatement } from '@/services/statementGenerator';
import { StatementFormat, StatementCustomer } from '@/types/statement';
import StatementFormatDialog from './StatementFormatDialog';

/**
 * Presentational component for statement download. No API usage.
 * Data must be provided by a trader or CRM container that uses the appropriate hooks.
 */
export interface StatementDownloadButtonUIProps {
    transactions: any[];
    trades: any[];
    customerProfile: StatementCustomer | undefined;
    isLoading: boolean;
    variant?: 'mobile' | 'desktop';
    className?: string;
}

export const StatementDownloadButtonUI: React.FC<StatementDownloadButtonUIProps> = ({
    transactions,
    trades,
    customerProfile,
    isLoading,
    variant = 'desktop',
    className = '',
}) => {
    const { t } = useTranslation('mobile');
    const { enqueueSnackbar } = useSnackbar();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    const handleOpenDialog = () => {
        if (!transactions?.length && !trades?.length) {
            enqueueSnackbar(t('statementNoActivity'), {
                variant: 'warning',
                autoHideDuration: 3000,
            });
            return;
        }

        setIsDialogOpen(true);
    };

    const handleFormatSelected = async (format: StatementFormat) => {
        setIsGenerating(true);

        try {
            if (!customerProfile) {
                enqueueSnackbar(t('statementProfileError'), {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
                return;
            }

            await downloadStatement(format, transactions, trades, customerProfile);

            enqueueSnackbar(t('statementDownloadedAs', { format: format.toUpperCase() }), {
                variant: 'success',
                autoHideDuration: 3000,
            });

            setIsDialogOpen(false);
        } catch (error) {
            console.error('Statement generation error:', error);
            enqueueSnackbar(t('statementFailed', { format: format.toUpperCase() }), {
                variant: 'error',
                autoHideDuration: 5000,
            });
        } finally {
            setIsGenerating(false);
        }
    };

    if (variant === 'mobile') {
        return (
            <>
                <button onClick={handleOpenDialog} disabled={isLoading} className={className}>
                    {isLoading ? (
                        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                    ) : (
                        <FileText className="w-6 h-6 text-white cursor-pointer hover:text-blue-400 transition-colors" />
                    )}
                </button>

                <StatementFormatDialog
                    open={isDialogOpen}
                    onClose={() => setIsDialogOpen(false)}
                    onFormatSelected={handleFormatSelected}
                    isGenerating={isGenerating}
                />
            </>
        );
    }

    return (
        <>
            <Button
                variant="outline"
                className={`flex items-center gap-2 ${className}`}
                onClick={handleOpenDialog}
                disabled={isLoading}
            >
                {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                    <FileText className="h-4 w-4" />
                )}
                {t('downloadStatement')}
            </Button>

            <StatementFormatDialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onFormatSelected={handleFormatSelected}
                isGenerating={isGenerating}
            />
        </>
    );
};

// --- Trader-only container: uses trader hooks only ---
interface StatementDownloadButtonProps {
    variant?: 'mobile' | 'desktop';
    className?: string;
}

/**
 * Trader portal statement download. Uses trader API only (useTransactions, useTrades, useGetCustomerProfile).
 * Do not use in CRM context; use CrmStatementDownloadButton instead.
 */
const StatementDownloadButton: React.FC<StatementDownloadButtonProps> = ({
    variant = 'desktop',
    className = '',
}) => {
    const { data: profileData } = useGetCustomerProfile();
    const { data: transactionsData, isLoading: transactionsLoading } = useTransactions({
        per_page: 10000,
        status: 'completed',
    });
    const { data: tradesData, isLoading: tradesLoading } = useTrades({
        status: 'closed',
        per_page: 10000,
    });

    const transactions = transactionsData?.data || [];
    const trades = tradesData?.trades || [];
    const customerProfile = profileData?.data;
    const isLoading = transactionsLoading || tradesLoading;

    return (
        <StatementDownloadButtonUI
            transactions={transactions}
            trades={trades}
            customerProfile={customerProfile}
            isLoading={isLoading}
            variant={variant}
            className={className}
        />
    );
};

export default StatementDownloadButton;
